/**
 * R-RAMBLER operator panel data layer.
 *
 * Rambler does NOT mint aliases: one sellable unit per mailbox, and the scarce
 * resource is the (mailbox, site) right. The operator therefore needs two
 * numbers the alias-model pages never show: how much stock exists, and what
 * percentage of it has been consumed for which site.
 *
 * Endpoint: GET /admin/providers/rambler/site-usage (admin-guarded; served only
 * for a bundle that declares siteScopedRights — mail.com/gmx answer 404
 * provider_not_supported).
 */
import { callAdminApi, resolveBaseUrl, type ApiResult } from '../../api/client.js';

export interface RamblerStockUnits {
  readonly total: number;
  readonly available: number;
  readonly leased: number;
  readonly reserved: number;
  readonly burnedPairs: number;
  readonly mailboxesWithBurns: number;
}

export interface RamblerSiteUsageRow {
  readonly site: string;
  readonly burnedMailboxes: number;
  readonly servedMailboxes: number;
  /** burnedMailboxes / units.total × 100 — "stokların %kaçı bu site için". */
  readonly shareOfStock: number;
  readonly lastBurnedAt: string | null;
}

export interface RamblerSiteUsage {
  readonly providerId: string;
  readonly units: RamblerStockUnits;
  readonly accounts: number;
  readonly sites: readonly RamblerSiteUsageRow[];
}

export async function fetchRamblerSiteUsage(
  signal?: AbortSignal,
): Promise<ApiResult<RamblerSiteUsage>> {
  return callAdminApi<RamblerSiteUsage>(
    { baseUrl: resolveBaseUrl(), signal },
    { path: '/admin/providers/rambler/site-usage' },
  );
}

/** Pure helper (unit-tested): share of a partial stock count, one decimal. */
export function sharePercent(part: number, total: number): number {
  if (!Number.isFinite(part) || !Number.isFinite(total) || total <= 0) return 0;
  return Math.round((part / total) * 1000) / 10;
}

/** Sellable share of the whole stock, one decimal. */
export function availableShare(units: RamblerStockUnits): number {
  return sharePercent(units.available, units.total);
}
